import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import axios from "./axios";
import { billingKeys } from "./billing";

/**
 * Plan feature administration.
 *
 * The feature list a plan sells ("Everything in Essential", "Geo-location",
 * ...) is data on `plans.features`, not a constant in either frontend. This
 * module is what lets an administrator change it next to pricing, which is the
 * whole point: what a tier includes is a commercial decision and must not
 * require a release.
 */

export const featureKeys = {
  catalogue: () => ["billing", "features", "catalogue"] as const,
};

export interface FeatureCatalogueEntry {
  key: string;
  label: string;
  description: string | null;
  tier: "saas" | "ai_addon" | string;
}

export interface FeatureCatalogueGroup {
  group: string;
  label: string;
  features: FeatureCatalogueEntry[];
}

/** "Everything in X" marker, one per existing plan. */
export interface InheritanceOption {
  key: string;
  slug: string;
  label: string;
  tier: string;
}

export interface PlanFeatureState {
  id: number;
  slug: string;
  name: string;
  tier: string;
  is_active: boolean;
  sort_order: number;
  /** What is stored, including `everything_in_*` markers. */
  features: string[];
  /** What the customer actually gets, markers expanded. */
  effective_features: string[];
}

export interface FeatureCatalogueResponse {
  groups: FeatureCatalogueGroup[];
  inheritance: InheritanceOption[];
  plans: PlanFeatureState[];
}

export const useGetFeatureCatalogue = (enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: featureKeys.catalogue(),
    enabled,
    queryFn: async (): Promise<FeatureCatalogueResponse> => {
      const { data } = await axios.get("/admin/billing/features");
      return data?.data ?? data;
    },
  });

  return {
    catalogue: data ?? { groups: [], inheritance: [], plans: [] },
    ...res,
  };
};

/**
 * Replace a plan's feature list.
 *
 * Goes through the general plan-update endpoint, which rejects pricing fields
 * and validates every key against the server-side FeatureRegistry - a typo is
 * a 422 with the offending key named, not a silent capability outage.
 */
export const useUpdatePlanFeatures = (onSuccessCallback?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { planId: number; features: string[] }) => {
      const { data } = await axios.put(
        `/admin/billing/plans/${payload.planId}`,
        { features: payload.features }
      );
      return data?.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.catalogue() });
      queryClient.invalidateQueries({ queryKey: billingKeys.plans() });
      toast.success(
        t("billing.featuresSaved", "Fonctionnalites de l'offre mises a jour")
      );
      onSuccessCallback?.();
    },
    onError: (error: any) => {
      // The API returns a per-key `errors` array; surfacing the first one is
      // far more actionable than "request failed".
      const errors: string[] = error?.response?.data?.errors ?? [];
      toast.error(
        errors[0] ||
          error?.response?.data?.message ||
          t("billing.featuresSaveError", "Echec de la mise a jour")
      );
    },
  });
};
